import React, { useCallback, useState, FC, useEffect, useRef } from "react";
import Actions from "../listing/Actions";
import {
  ControlsContainer,
  Divider,
  FilterButton,
  HeaderContainer,
  SelectButton,
  Subtitle,
  Title,
  TitleCount,
  TitleGroup,
  TitleWrapper,
} from "../listing/AMSListing";
import FilterIcon from "../../assets/icons/FilterIcon";
import DateRangeFilter from "../listing/DateRangeFilter";
import AdvanceSearchDropDown from "../listing/AdvanceSearchDropDown";
import ExpandableSearch from "../listing/ExpandableSearch";
import SelectIcon from "../../assets/icons/SelectIcon";
import { debounce } from "lodash";
import { Column, TableConfig } from "../../types/types";
import ModalWrapper from "../ModelWrapper";
import ColumnIdentifier from "../listing/ColumnIdentifier";
import Filters from "../listing/Filters";
import FolderIcon from "../../assets/icons/FolderIcon";
import { ActionWrapper } from "../../features/searchQuery/QueryCreation";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import {
  FormatOptions,
  Options,
} from "../../features/searchQuery/QueryResultsListing";
import styled from "styled-components";
import { CorporateTheme } from "../../utils/themes";
import CrossIcon from "../../assets/icons/CrossIcon";
import { useTheme } from "../../middleware/ThemeContext";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import ColumnIdentifierIcon from "../../assets/icons/ColumnIdentifierIcon";
import ExportIcon from "../../assets/icons/ExportIcon";
import MergeIcon from "../../assets/icons/MergeIcon";

const formatLocalDateTime = (date: Date | null) => {
      if (!date) return null;

      const pad = (num: number) => num.toString().padStart(2, '0');

      const yyyy = date.getFullYear();
      const mm = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      const hh = pad(date.getHours());
      const min = pad(date.getMinutes());
      const ss = pad(date.getSeconds());

      return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    };

interface TableProps {
  title: string;
  subtitle: string;
  columns?: Column[];
  data?: any[];
  totalItems: number;
  loading?: boolean;
  noDataTitle?: string;
  noDataSubtitle?: string;
  routeKey: string;
  staticColumns?: Column[];
  advanceSearch?: boolean;
  setButtonAction?: (val: boolean) => void;
  buttonAction?: boolean;
  queryFilters?: boolean;
  disabled?: boolean;
  filterDisabled?: boolean;

  onFetchRequested?: (params: {
    page?: number;
    perPage?: number;
    sort?: { field: string; direction: "ASC" | "DESC" };
    query?: string;
    filters?: Record<string, any>;
    visibleColumns?: string[];
  }) => void;
  filterGroups?: {
    dataType?: string;
    key: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  setInitialFilters?: any;
  filter?: boolean;
  action?: boolean;
  isBorder?: boolean;
}

const ComponentHeader: FC<TableProps> = ({
  title,
  totalItems,
  subtitle,
  data = [],
  routeKey,
  columns = [],
  staticColumns,
  setInitialFilters,
  filterGroups = [],
  onFetchRequested,
  advanceSearch,
  filter,
  action,
  setButtonAction,
  buttonAction,
  queryFilters,
  disabled,
  isBorder,
  filterDisabled
}) => {
  const { theme } = useTheme();
  const STORAGE_KEY = `tableConfig_${routeKey}`;
  const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    const hasFormTitle = title?.includes("Form");

    return saved
      ? JSON.parse(saved)
      : {
        visibleColumns: columns
          .filter((col: any) => (hasFormTitle ? true : col?.id !== "id"))
          .map((col: any) => col?.id),
        dynamicColumns: [],
        page: 1,
        perPage: 10,
        sort: undefined,
        filters: {},
        query: "",
        columnOrder: columns.map((col) => col.id),
      };
  });

  const totalPages = Math.ceil(totalItems / tableConfig.perPage);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isFiltersOpen, setisFiltersOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [isColumnIdentifierModalOpen, setIsColumnIdentifierModalOpen] =
    useState(false);
  const [startDate, endDate] = dateRange;
  const [showCheckboxColumn, setShowCheckboxColumn] = useState(false);
  const [filterOptions, setFilterOptions] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const filterRef = useRef(null);

  const debouncedSetQuery = useCallback(
    debounce((value: string) => {
      setInitialFilters((prev: any) => ({
        ...prev,
        query: value,
      }));
    }, 300),
    []
  );

  const hasAppliedFilters = Object.values(tableConfig.filters).some(
    (filterGroup) => Array.isArray(filterGroup) && filterGroup?.length > 0
  );
  const handleSelectAll = useCallback(() => {
    setSelectedIds(data.map((item) => item.id));
  }, [data]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleToggleColumnIdentifier = () => {
    setIsColumnIdentifierModalOpen(true);
  };

  const handleSaveColumns = (
    dynamicColumns: Column[],
    selectedColumns: string[]
  ) => {
    const newVisibleColumns =
      selectedColumns && selectedColumns.length > 0
        ? selectedColumns
        : staticColumns
          ? staticColumns.map((col) => col.id)
          : [];
    const newColumnOrder = newVisibleColumns;
    setTableConfig((prev: TableConfig) => ({
      ...prev,
      dynamicColumns: dynamicColumns,
      visibleColumns: newVisibleColumns.filter((col) => col !== "id"),
      columnOrder: newColumnOrder,
      page: 1,
    }));
    setIsColumnIdentifierModalOpen(false);
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (e.type === "beforeunload") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tableConfig));
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      sessionStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY);
    };
  }, [tableConfig, STORAGE_KEY]);

  useEffect(() => {
    localStorage.setItem(routeKey, JSON.stringify(tableConfig));
  }, [tableConfig]);

  const debouncedFetch = useCallback(
    debounce(
      (config: Parameters<NonNullable<typeof onFetchRequested>>[0]) =>
        (onFetchRequested ?? (() => { }))(config),
      300
    ),
    [onFetchRequested]
  );

  useEffect(() => {
    debouncedFetch({
      page: tableConfig.page,
      perPage: tableConfig.perPage,
      sort: tableConfig.sort,
      query: tableConfig.query,
      filters: {
        ...tableConfig.filters,
        startDate: formatLocalDateTime(startDate),
        endDate: formatLocalDateTime(endDate),
      },
      visibleColumns: tableConfig.visibleColumns,
    });

    setInitialFilters &&
      setInitialFilters((prev: any) => ({
        ...prev,

        filters: {
          startDate: formatLocalDateTime(startDate),
          endDate: formatLocalDateTime(endDate),
        },
      }));
  }, [tableConfig, debouncedFetch, startDate, endDate]);

  function useOutsideAlerter(ref: any) {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setFilterOptions(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useOutsideAlerter(filterOptions && filterRef);

  return (
    <div className="px-3" style={{ backgroundColor: theme.colors.contentBackground }}>
      <HeaderContainer theme={theme} isBorder={isBorder}>
        <TitleGroup>
          <TitleWrapper>
            <Title theme={theme}>{title}</Title>
            <TitleCount theme={theme}>({totalItems})</TitleCount>
          </TitleWrapper>
          <Subtitle theme={theme}>{subtitle}</Subtitle>
        </TitleGroup>
        <ControlsContainer>
          {/* <SelectButton
            onClick={() => setShowCheckboxColumn(!showCheckboxColumn)}
            $active={showCheckboxColumn}
          >
            <SelectIcon />
            Select
          </SelectButton> */}
          {/* <Divider /> */}
          {queryFilters && (
            <ExpandableSearch
              disabled={disabled}
              value={tableConfig.query}
              onChange={(e: any) => {
                setTableConfig((prev) => ({ ...prev, query: e.target.value }));

                setInitialFilters && debouncedSetQuery(e.target.value);
              }}
            />
          )}
          {advanceSearch && <AdvanceSearchDropDown />}
          {queryFilters && (
            <>
              {" "}
              <Divider />
              <DateRangeFilter
                disabled={disabled}
                label="Last Run Date"
                routeKey={routeKey}
                onDateChange={setDateRange}
                startDate={startDate}
                endDate={endDate}
                hideLabel
                showResetButton
              />
            </>
          )}
          {queryFilters && (
            <>
              <Divider />
              <ButtonWrapper
                theme={theme}
                disabled={filterDisabled}
                className="cursor-pointer position-relative"
                onClick={() => { if (!disabled) setFilterOptions(true) }}
                ref={filterRef}
              >
                {filterValue ? filterValue : "Scope"}{" "}
                {filterValue && (
                  <CrossIcon
                    onClick={() => {
                      setInitialFilters((prev: any) => ({
                        ...prev,
                        scope: undefined,
                      }));
                      setFilterValue("");
                    }}
                    size={18}
                  />
                )}{" "}
                <ChevronDownIcon color={theme.colors.contentText} />
                {filterOptions && (
                  <FormatOptions theme={theme}>
                    {[
                      { label: "Contact", value: "contact" },
                      // { label: "CRM", value: "crm" },
                      { label: "Organization", value: "organization" },
                    ]?.map((formats, ind) => (
                      <>
                        <Options
                          theme={theme}
                          className="py-2"
                          onClick={() => {
                            setInitialFilters((prev: any) => ({
                              ...prev,
                              scope: formats.value,
                            }));
                            setFilterValue(formats?.label);
                          }}
                        >
                          {formats?.label}
                        </Options>
                        {ind < 3 - 1 && <hr className="my-0" />}
                      </>
                    ))}
                  </FormatOptions>
                )}
              </ButtonWrapper>
            </>
          )}

          {filter && (
            <FilterButton
              disabled={disabled}
              theme={theme}
              onClick={() => setisFiltersOpen(!isFiltersOpen)}
              $active={
                isFiltersOpen ||
                hasAppliedFilters ||
                !!localStorage.getItem("persistedDateRange")
              }
            >
              <FilterIcon />
              Filters
            </FilterButton>
          )}
          {action && (
            <>
              {" "}
              <Divider />
              {/* <Actions
                title={title}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                onExport={handleExport ?? (() => { })}
                onToggleColumnIdentifier={handleToggleColumnIdentifier}
                selectedCount={selectedIds?.length}
                totalCount={totalItems}
                onToggleSelectRecords={() =>
                  setShowCheckboxColumn(!showCheckboxColumn)
                }
                showCheckboxColumn={showCheckboxColumn}
              /> */}
              <Actions
                actions={
                  [
                    {
                      type: "deleteSelected",
                      label: "Delete Selected",
                      icon: <DeleteIcon />,
                      onClick: () => { },
                      visible: title?.includes("Form") && showCheckboxColumn,
                    },
                    {
                      type: "toggleColumnIdentifier",
                      label: "Column Identifier",
                      icon: (
                        <ColumnIdentifierIcon color={theme.CorporateTheme.colors.textSecondary} />
                      ),
                      onClick: handleToggleColumnIdentifier,
                      visible: !!title && !title.includes("Form") && !title.includes("Field"),
                    },
                    {
                      type: "export",
                      label: "Export Data",
                      icon: <ExportIcon color={theme.CorporateTheme.colors.textSecondary} />,
                      onClick: () => console.log("export"),
                      visible: true,
                    },
                    {
                      type: "toggleSelect",
                      label: showCheckboxColumn ? "Hide Selection" : "Show Selection",
                      icon: (
                        <ColumnIdentifierIcon color={theme.CorporateTheme.colors.textSecondary} />
                      ),
                      onClick: () => setShowCheckboxColumn(!showCheckboxColumn),
                      visible: true,
                    },
                    {
                      type: "mergeContacts",
                      label: "Merge Contacts",
                      icon: <MergeIcon color={theme.CorporateTheme.colors.textSecondary} />,
                      onClick: () => { },
                      visible: title?.includes("Contacts Listing"),
                    },
                  ]
                }
              />
            </>
          )}
          {/* {!action && (
            <>
              {" "}
              <Divider />
              <SelectButton
                onClick={() =>
                  setButtonAction && setButtonAction(!buttonAction)
                }
                $active={buttonAction}
              >
                <FolderIcon />
                Create Folder
              </SelectButton>
            </>
          )} */}
        </ControlsContainer>
      </HeaderContainer>

      {(isFiltersOpen ||
        hasAppliedFilters ||
        localStorage.getItem(`persistedDateRange_${routeKey}`)) && (
          <Filters
            query
            setShowFilters={setisFiltersOpen}
            isFiltersOpen={isFiltersOpen}
            filterGroups={filterGroups ?? []}
            filters={tableConfig.filters}
            routeKey={routeKey}
            onFilterChange={(newFilters) =>
              setTableConfig((prev) => ({
                ...prev,
                filters: newFilters,
                page: 1,
              }))
            }
          />
        )}

      {isColumnIdentifierModalOpen && (
        <ModalWrapper onClose={() => setIsColumnIdentifierModalOpen(false)}>
          <ColumnIdentifier
            entityType={routeKey}
            onClose={() => setIsColumnIdentifierModalOpen(false)}
            onSave={handleSaveColumns} // Use the updated handler
            availableColumns={tableConfig?.dynamicColumns
              .filter((col) => typeof col !== "string")
              .map((col) => (col as Column).id)}
            initialSelectedColumns={
              tableConfig?.dynamicColumns?.length > 0
                ? tableConfig.visibleColumns
                : []
            }
          />
        </ModalWrapper>
      )}
    </div>
  );
};

export default ComponentHeader;

export const ButtonWrapper = styled(ActionWrapper)(({ theme, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'auto',
  gap: theme.CorporateTheme.spacing.sm,
  border: `1px solid ${theme.CorporateTheme.colors.border}`,
  borderRadius: theme.CorporateTheme.borderRadius,
  padding: `5px ${theme.CorporateTheme.spacing.sm}`,
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.CorporateTheme.colors.filterBG,
  cursor: disabled ? 'not-allowed' : 'pointer',
  maxHeight: '39px',
}));
